import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import Logo from '@/components/Logo';
import { Close as CloseIcon } from '@/components/icons';
import AccountFooter from './AccountFooter';
import { NAV } from './Sidebar';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MobileNav({ open, onClose }: Props) {
  // Lock body scroll + close on Escape while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', esc);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-bg-deep/50 backdrop-blur-[2px] z-40 md:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed top-0 left-0 bottom-0 w-[260px] bg-bg-elev border-r border-fg/10 shadow-xl z-50 flex flex-col md:hidden"
            initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 360, damping: 36 }}
            role="dialog" aria-label="Navigation"
          >
            <div className="h-12 flex items-center justify-between px-3 border-b border-fg/10">
              <Logo />
              <button
                type="button"
                onClick={onClose}
                className="icon-btn"
                aria-label="Close menu"
              >
                <CloseIcon />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={onClose}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <span className="nav-icon">{n.icon}</span>
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Account footer — shared with Sidebar. Pass onClose so the
                drawer dismisses before the logout redirect fires. */}
            <AccountFooter onSignedOut={onClose} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

